import { useClient } from '@cortex/react-query';
import type { GroundTruth, WorkflowListItem, WorkflowVersion } from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
} from 'react';
import { createPortal } from 'react-dom';

import { InfoHint } from '../../components/common/InfoHint';
import { InlineEditableText } from '../../components/common/InlineEditableText';
import { JsonObjectBuilder } from '../../components/common/JsonObjectBuilder';
import { JsonViewer } from '../../components/common/JsonViewer';
import { PageHeader } from '../../components/common/PageHeader';
import { Spinner } from '../../components/common/Spinner';
import { TagEditor } from '../../components/common/TagEditor';
import { ViewToggle } from '../../components/common/ViewToggle';
import { routes } from '../../lib/constants';
import { formatDateTime } from '../../lib/format';
import { controlsRow, sortButtonStyle, tdStyle, thStyle } from '../../lib/table-styles';
import { useViewMode } from '../../lib/view-mode';

export const Route = createFileRoute('/ground-truth/')({
  component: GroundTruthListPage,
  validateSearch: (raw: Record<string, unknown>): { workflowId?: string } => ({
    workflowId: typeof raw.workflowId === 'string' ? raw.workflowId : undefined,
  }),
});

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

// ── Card + grid chrome ───────────────────────────────────────────────────────
// Identical to the `cardStyle`/`grid` used by the /workflows card grid so the
// ground-truth picker cards and per-entry grid cards read as siblings of the
// Workflows page. Kept as a local copy (rather than an imported constant) to
// avoid coupling two route files through a shared card module.

const cardGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: 'var(--sp-3)',
};

const cardChrome: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-2)',
  padding: 'var(--sp-3)',
  background: 'var(--surface)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius)',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.18), 0 1px 2px rgba(0, 0, 0, 0.24)',
  cursor: 'pointer',
  textAlign: 'left',
  color: 'inherit',
  font: 'inherit',
  width: '100%',
  transition: 'box-shadow 120ms ease, transform 120ms ease, border-color 120ms ease',
};

/** Subtle border-color lift on hover, applied via handlers so it works on the
 *  `<button>` card chrome without a dedicated CSS class. */
const cardHoverIn = (e: { currentTarget: HTMLElement }) => {
  e.currentTarget.style.borderColor = 'var(--border)';
};
const cardHoverOut = (e: { currentTarget: HTMLElement }) => {
  e.currentTarget.style.borderColor = 'var(--border-subtle)';
};

function GroundTruthListPage() {
  const search = Route.useSearch();
  const nav = Route.useNavigate();

  // Mirror the URL param into local state for the picker.
  const workflowId = search.workflowId ?? '';
  const setWorkflowId = (id: string) => {
    nav({ search: id ? { workflowId: id } : {} }).catch((err) => console.error(err));
  };

  if (!workflowId) {
    return <WorkflowPicker onPick={setWorkflowId} />;
  }
  return <EditableList workflowId={workflowId} onChangeWorkflow={() => setWorkflowId('')} />;
}

// ─── Workflow picker (landing) ──────────────────────────────────────────────

/**
 * Returns an empty value matching a JSON Schema property's declared `type`.
 * Used by the quick-add form to pre-fill input/expected with the keys the
 * workflow author declared, so the user only fills in values.
 */
function blankForJsonSchemaType(prop: unknown): unknown {
  if (!prop || typeof prop !== 'object') return '';
  const t = (prop as Record<string, unknown>).type;
  if (Array.isArray(t)) {
    // type: ['string', 'null'] etc — pick the first non-null entry.
    const first = t.find((x) => x !== 'null') ?? t[0];
    return blankForJsonSchemaType({ type: first });
  }
  if (t === 'number' || t === 'integer') return 0;
  if (t === 'boolean') return false;
  if (t === 'array') return [];
  if (t === 'object') return {};
  if (t === 'null') return null;
  return '';
}

// ── Schema-vs-value warning helper ─────────────────────────────────────────
// Compares a value's top-level keys against a JSON Schema's `properties` +
// `required`. Returns null when there's no schema, no mismatch, or the
// schema isn't a top-level object schema we can inspect.
export interface ISchemaWarning {
  /** Keys declared in schema.properties that are missing from value. */
  missing: string[];
  /** Subset of `missing` listed in schema.required. */
  missingRequired: string[];
  /** Keys in value that aren't declared in schema.properties. */
  extra: string[];
}
function compareToSchema(
  schema: Record<string, unknown> | null | undefined,
  value: Record<string, unknown> | null | undefined,
): ISchemaWarning | null {
  if (!schema || typeof schema !== 'object') return null;
  const props = schema.properties as Record<string, unknown> | undefined;
  if (!props || typeof props !== 'object') return null;
  const required = Array.isArray(schema.required) ? (schema.required as string[]) : [];
  const schemaKeys = new Set(Object.keys(props));
  // A null/missing value is treated as an empty object — every declared key
  // shows up as "missing" so the user sees what the contract expects even
  // before they've started filling things in (e.g. unlabeled entries).
  const valueKeys = new Set(
    value && typeof value === 'object' && !Array.isArray(value) ? Object.keys(value) : [],
  );
  const missing: string[] = [];
  for (const k of schemaKeys) if (!valueKeys.has(k)) missing.push(k);
  const extra: string[] = [];
  for (const k of valueKeys) if (!schemaKeys.has(k)) extra.push(k);
  if (missing.length === 0 && extra.length === 0) return null;
  return {
    missing: missing.sort(),
    missingRequired: missing.filter((k) => required.includes(k)).sort(),
    extra: extra.sort(),
  };
}

/**
 * Bundle of schemas available for warning checks. Each field may be null when
 * the workflow author didn't publish a schema at that level.
 */
export interface IWorkflowSchemas {
  /** Workflow trigger input schema (applies to every entry). */
  input: Record<string, unknown> | null;
  /** Workflow final output schema (applies to workflow-scoped entries). */
  output: Record<string, unknown> | null;
  /** nodeKey → that node's `outputSchema` (applies to node-scoped entries). */
  byNode: Map<string, Record<string, unknown>>;
}

function WorkflowPicker({ onPick }: { onPick: (id: string) => void }) {
  const client = useClient();
  const workflows = useQuery({
    queryKey: ['workflows', 'all-for-gt'],
    queryFn: () =>
      client.http.get('v1/workflows?limit=200').json<IListEnvelope<WorkflowListItem>>(),
  });

  // Per-workflow GT count, so the picker shows how much each workflow already
  // has labeled. One small request per workflow — fine for ~200 workflows.
  const counts = useQuery({
    queryKey: ['ground-truth', 'counts', (workflows.data?.items ?? []).map((w) => w.id).join(',')],
    enabled: !!workflows.data,
    queryFn: async () => {
      const items = workflows.data?.items ?? [];
      const entries = await Promise.all(
        items.map(async (w) => {
          const r = await client.http
            .get(`v1/ground-truth?workflowId=${encodeURIComponent(w.id)}&limit=1`)
            .json<IListEnvelope<GroundTruth>>();
          return [w.id, r.total] as const;
        }),
      );
      return Object.fromEntries(entries) as Record<string, number>;
    },
  });

  const items = workflows.data?.items ?? [];

  // Split into "has GT" / "needs GT" using the per-workflow counts. While
  // counts are loading we treat every workflow as "unknown" (rendered in
  // the top section as a neutral fallback) — once counts come back the
  // split snaps into place. Workflows whose count is 0 land in the bottom
  // "Need Ground Truth" section.
  const { withGt, needGt } = useMemo(() => {
    const w: WorkflowListItem[] = [];
    const n: WorkflowListItem[] = [];
    for (const wf of items) {
      const c = counts.data?.[wf.id];
      if (c === undefined)
        w.push(wf); // unknown → top section as placeholder
      else if (c > 0) w.push(wf);
      else n.push(wf);
    }
    return { withGt: w, needGt: n };
  }, [items, counts.data]);

  return (
    <>
      <PageHeader title="Pick a workflow" />
      {workflows.isLoading ? (
        <Spinner />
      ) : items.length === 0 ? (
        <div className="card">
          <div className="card-body">
            <p>No workflows yet — create one first.</p>
            <Link to={routes.WORKFLOWS} className="btn btn--primary btn--sm">
              Go to workflows
            </Link>
          </div>
        </div>
      ) : (
        <>
          <section style={{ marginBottom: 'var(--sp-4)' }}>
            {withGt.length === 0 ? (
              <div className="t-muted t-sm" style={{ padding: 'var(--sp-2) 0' }}>
                No workflows have ground truth entries yet.
              </div>
            ) : (
              <WorkflowCardGrid items={withGt} counts={counts.data} onPick={onPick} />
            )}
          </section>
          {needGt.length > 0 ? (
            <section>
              <h2 className="t-h4" style={{ marginBottom: 'var(--sp-2)' }}>
                Need Ground Truth
              </h2>
              <p className="t-muted t-sm" style={{ marginBottom: 'var(--sp-2)' }}>
                These workflows have zero ground truth entries — labeling them unlocks evaluations
                and regression scoring.
              </p>
              <WorkflowCardGrid items={needGt} counts={counts.data} onPick={onPick} />
            </section>
          ) : null}
        </>
      )}
    </>
  );
}

function WorkflowCardGrid({
  items,
  counts,
  onPick,
}: {
  items: WorkflowListItem[];
  counts: Record<string, number> | undefined;
  onPick: (id: string) => void;
}) {
  return (
    <div style={cardGrid}>
      {items.map((w) => {
        const n = counts?.[w.id];
        const labeled = n !== undefined && n > 0;
        return (
          <button
            key={w.id}
            type="button"
            onClick={() => onPick(w.id)}
            style={cardChrome}
            onMouseEnter={cardHoverIn}
            onMouseLeave={cardHoverOut}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 'var(--sp-2)',
              }}
            >
              <span
                style={{
                  fontSize: 'var(--fs-sm)',
                  fontWeight: 'var(--fw-med)',
                  color: 'var(--text-primary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
                title={w.name}
              >
                {w.name}
              </span>
              <span
                className={labeled ? 'fk-badge--completed' : 'fk-badge--pending'}
                style={{ flexShrink: 0 }}
              >
                {n === undefined ? '…' : n === 0 ? 'No entries' : `${n} labeled`}
              </span>
            </div>
            <div className="t-xs" style={{ color: 'var(--text-secondary)' }}>
              {n === undefined
                ? 'Counting entries…'
                : n === 0
                  ? 'No ground truth yet — click to start labeling'
                  : `${n} ground truth ${n === 1 ? 'entry' : 'entries'}`}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ─── Editable list (workflow selected) ──────────────────────────────────────

function EditableList({
  workflowId,
  onChangeWorkflow,
}: {
  workflowId: string;
  onChangeWorkflow: () => void;
}) {
  const client = useClient();
  const qc = useQueryClient();

  const workflows = useQuery({
    queryKey: ['workflows', 'all-for-gt'],
    queryFn: () =>
      client.http.get('v1/workflows?limit=200').json<IListEnvelope<WorkflowListItem>>(),
  });
  const workflow = workflows.data?.items.find((w) => w.id === workflowId);
  const nodes = workflow?.graphSummary?.nodes ?? [];

  const gt = useQuery({
    queryKey: ['ground-truth', workflowId],
    queryFn: () =>
      client.http
        .get(`v1/ground-truth?workflowId=${encodeURIComponent(workflowId)}&limit=200`)
        .json<IListEnvelope<GroundTruth>>(),
  });

  // ── Workflow schemas. Single source of truth: the *latest* WorkflowVersion
  //   (versions are returned newest-first by versionNumber). This intentionally
  //   ignores `currentVersionId` so a DRAFT version's updated contract drives
  //   the warnings even before promotion. Three layers are pulled out:
  //   - `input`:   latestVersion.inputSchema (workflow trigger input)
  //   - `output`:  latestVersion.outputSchema (workflow final output)
  //   - `byNode`:  nodeKey → that node's outputSchema (read from the graph)
  //   Any layer the author didn't publish stays null/empty and is silently
  //   skipped during compareToSchema().
  const versionsQ = useQuery({
    queryKey: ['workflow-versions', workflowId],
    queryFn: () =>
      client.http
        .get(`v1/workflows/${encodeURIComponent(workflowId)}/versions`)
        .json<WorkflowVersion[]>(),
  });
  const schemas = useMemo<IWorkflowSchemas>(() => {
    const byNode = new Map<string, Record<string, unknown>>();
    const latest = versionsQ.data?.[0]; // versions are sorted newest-first
    const graph = latest?.graph as
      | { nodes?: Array<{ nodeKey?: string; outputSchema?: Record<string, unknown> }> }
      | undefined;
    for (const n of graph?.nodes ?? []) {
      if (n.nodeKey && n.outputSchema && Object.keys(n.outputSchema).length > 0) {
        byNode.set(n.nodeKey, n.outputSchema);
      }
    }
    return {
      input: (latest?.inputSchema as Record<string, unknown> | null | undefined) ?? null,
      output: (latest?.outputSchema as Record<string, unknown> | null | undefined) ?? null,
      byNode,
    };
  }, [versionsQ.data]);

  // Distinct tags currently in use on this workflow's ground truth, pulled
  // from the dedicated SELECT-DISTINCT-UNNEST endpoint. Used both for the
  // tag-filter popover and as autocomplete suggestions in TagInput editors.
  const distinctTagsQ = useQuery({
    queryKey: ['ground-truth-tags', workflowId],
    queryFn: () =>
      client.http
        .get(`v1/ground-truth/tags?workflowId=${encodeURIComponent(workflowId)}`)
        .json<{ tags: string[] }>(),
  });
  const distinctTags = distinctTagsQ.data?.tags ?? [];

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['ground-truth', workflowId] }).catch((err) =>
      console.error(err),
    );
    // Refresh the distinct-tags list too — adding/editing/removing entries
    // can change the set of tags in use.
    qc.invalidateQueries({ queryKey: ['ground-truth-tags', workflowId] }).catch((err) =>
      console.error(err),
    );
  };

  // Quick-add open state is lifted here so the top-bar "Add entry" button
  // can flip it open and scroll the row into view.
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const quickAddRef = useRef<HTMLDivElement | null>(null);
  const openQuickAdd = () => {
    setQuickAddOpen(true);
    // Wait a tick so the expanded form is in the DOM before scrolling.
    setTimeout(() => {
      quickAddRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 30);
  };

  return (
    <>
      <PageHeader
        title={workflow?.name ?? 'Workflow'}
        actions={
          <>
            <button type="button" onClick={onChangeWorkflow} className="btn btn--ghost btn--sm">
              Back
            </button>
            <Link to={routes.GROUND_TRUTH_BULK} className="btn btn--ghost btn--sm">
              Bulk import
            </Link>
            <button type="button" onClick={openQuickAdd} className="btn btn--primary btn--sm">
              Add entry
            </button>
          </>
        }
      />

      {gt.isLoading ? (
        <Spinner />
      ) : gt.error ? (
        <div className="alert alert--danger">Failed to load: {(gt.error as Error).message}</div>
      ) : (
        <GtTable
          entries={gt.data?.items ?? []}
          nodes={nodes}
          workflowId={workflowId}
          schemas={schemas}
          distinctTags={distinctTags}
          onChanged={invalidate}
          footer={
            <QuickAddRow
              workflowId={workflowId}
              nodes={nodes}
              entries={gt.data?.items ?? []}
              schemas={schemas}
              distinctTags={distinctTags}
              onAdded={invalidate}
              open={quickAddOpen}
              setOpen={setQuickAddOpen}
              containerRef={quickAddRef}
            />
          }
        />
      )}
    </>
  );
}

// ─── Generic icon filter (funnel icon + multi-select popover) ──────────────

interface IFilterOption {
  value: string;
  label: string;
  hint?: string;
  mono?: boolean;
  /** Optional group label inserted above this option (e.g. "NODES"). */
  group?: string;
}

function IconFilter({
  title,
  options,
  selected,
  onChange,
}: {
  title: string;
  options: IFilterOption[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!open) return;
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 4, left: r.left });
    const onClickOutside = (e: globalThis.MouseEvent) => {
      if (
        popRef.current &&
        !popRef.current.contains(e.target as Node) &&
        !btnRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = (val: string) => {
    const next = new Set(selected);
    if (next.has(val)) next.delete(val);
    else next.add(val);
    onChange(next);
  };

  const active = selected.size > 0;

  // Group options for rendering — keep insertion order, insert a group label
  // when the group string changes.
  const rendered: React.ReactNode[] = [];
  let lastGroup: string | undefined;
  for (const opt of options) {
    if (opt.group && opt.group !== lastGroup) {
      rendered.push(
        <div
          key={`g-${opt.group}`}
          className="t-xs"
          style={{
            color: 'var(--text-subtle, rgba(255,255,255,0.35))',
            padding: '6px 4px 2px',
            letterSpacing: 0.4,
          }}
        >
          {opt.group}
        </div>,
      );
      lastGroup = opt.group;
    }
    rendered.push(
      <FilterCheckbox
        key={opt.value}
        label={opt.label}
        hint={opt.hint}
        mono={opt.mono}
        checked={selected.has(opt.value)}
        onChange={() => toggle(opt.value)}
      />,
    );
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={title}
        title={title}
        style={{
          width: 22,
          height: 22,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 4,
          border: `1px solid ${active ? 'var(--accent-primary, #6ea8ff)' : 'var(--border-subtle, rgba(255,255,255,0.18))'}`,
          background: active ? 'rgba(110,168,255,0.12)' : 'transparent',
          color: active ? 'var(--accent-primary, #6ea8ff)' : 'var(--text-muted)',
          cursor: 'pointer',
          padding: 0,
          position: 'relative',
        }}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden={true}
        >
          <path d="M3 5h18l-7 9v6l-4-2v-4z" />
        </svg>
        {active ? (
          <span
            style={{
              position: 'absolute',
              top: -4,
              right: -4,
              minWidth: 14,
              height: 14,
              borderRadius: 7,
              background: 'var(--accent-primary, #6ea8ff)',
              color: '#000',
              fontSize: 9,
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 3px',
              lineHeight: 1,
            }}
          >
            {selected.size}
          </span>
        ) : null}
      </button>
      {open && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={popRef}
              role="dialog"
              aria-label={title}
              style={{
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                zIndex: 10_000,
                minWidth: 240,
                maxHeight: 360,
                overflowY: 'auto',
                background: 'var(--surface-1, #1a1a1a)',
                color: 'var(--text-primary, #fff)',
                border: '1px solid var(--border-subtle, rgba(255,255,255,0.18))',
                borderRadius: 'var(--radius-sm, 6px)',
                boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
                padding: 'var(--sp-2)',
                fontSize: 13,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '2px 4px 6px',
                  borderBottom: '1px solid var(--border-subtle, rgba(255,255,255,0.10))',
                  marginBottom: 4,
                }}
              >
                <span
                  className="t-xs"
                  style={{ color: 'var(--text-muted)', fontWeight: 600, letterSpacing: 0.4 }}
                >
                  {title.toUpperCase()}
                </span>
                <button
                  type="button"
                  onClick={() => onChange(new Set())}
                  className="t-xs"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: active
                      ? 'var(--accent-primary, #6ea8ff)'
                      : 'var(--text-subtle, rgba(255,255,255,0.35))',
                    cursor: active ? 'pointer' : 'default',
                    padding: 0,
                  }}
                  disabled={!active}
                >
                  Clear
                </button>
              </div>
              {rendered}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function FilterCheckbox({
  label,
  hint,
  checked,
  onChange,
  mono = false,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: () => void;
  mono?: boolean;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-2)',
        padding: '4px 4px',
        borderRadius: 4,
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--surface-2, rgba(255,255,255,0.04))';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span style={{ display: 'flex', flexDirection: 'column' }}>
        <span style={{ fontFamily: mono ? 'var(--font-mono)' : 'inherit', fontSize: 13 }}>
          {label}
        </span>
        {hint ? (
          <span className="t-xs" style={{ color: 'var(--text-muted)' }}>
            {hint}
          </span>
        ) : null}
      </span>
    </label>
  );
}

// ─── Scope filter (icon + popover) — kept as a thin wrapper ────────────────

// ─── Tag input (chip list + free-text entry) ───────────────────────────────

/**
 * Pure-CSS tag editor — wraps the kit's `.tag-input` markup. Normalizes to
 * lowercase/trimmed on commit so the server-side normalization (which is
 * authoritative) never has to push variants back. Commits a tag on Enter
 * or comma; Backspace at empty input removes the last chip.
 */
function TagInput({
  value,
  onChange,
  placeholder = 'Add tag…',
  suggestions,
  disabled,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  suggestions?: string[];
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState('');
  const inputId = useRef(`tag-input-${Math.random().toString(36).slice(2, 8)}`).current;

  const commit = (raw: string) => {
    const t = raw.trim().toLowerCase();
    if (!t) return;
    if (value.includes(t)) {
      setDraft('');
      return;
    }
    onChange([...value, t]);
    setDraft('');
  };

  const remove = (tag: string) => {
    onChange(value.filter((t) => t !== tag));
  };

  // Suggestions = every distinct tag on this workflow that isn't already
  // selected on THIS entry. We deliberately surface ALL of them (not a
  // top-N) so users can one-click any existing tag rather than retyping
  // it and risking a typo (e.g. "regresion" vs "regression"). When the
  // user starts typing we narrow to substring matches and bubble exact
  // prefix matches to the front for quick keyboard selection.
  const availableSuggestions = useMemo(() => {
    const draftNorm = draft.trim().toLowerCase();
    const pool = (suggestions ?? []).filter((s) => !value.includes(s));
    if (!draftNorm) return pool;
    const filtered = pool.filter((s) => s.includes(draftNorm));
    // Stable sort with prefix-matches first.
    filtered.sort((a, b) => {
      const ap = a.startsWith(draftNorm) ? 0 : 1;
      const bp = b.startsWith(draftNorm) ? 0 : 1;
      return ap - bp;
    });
    return filtered;
  }, [suggestions, value, draft]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div className="tag-input">
        {value.map((t) => (
          <span
            key={t}
            className="chip"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            {t}
            {!disabled ? (
              <button
                type="button"
                aria-label={`Remove tag ${t}`}
                onClick={() => remove(t)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: 12,
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            ) : null}
          </span>
        ))}
        <input
          id={inputId}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              // If there's a draft, prefer an exact suggestion match so the
              // user doesn't accidentally mint a near-duplicate ("regresion"
              // when "regression" already exists).
              const draftNorm = draft.trim().toLowerCase();
              if (!draftNorm && availableSuggestions[0]) {
                commit(availableSuggestions[0]);
                return;
              }
              const exact = availableSuggestions.find((s) => s === draftNorm);
              commit(exact ?? draft);
            } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
              const last = value[value.length - 1];
              if (last !== undefined) remove(last);
            }
          }}
          onBlur={() => {
            // Commit pending draft on blur so users who tab out keep their input.
            if (draft.trim()) commit(draft);
          }}
          placeholder={placeholder}
          disabled={disabled}
        />
      </div>
      {availableSuggestions.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
          <span
            className="t-mono-label"
            style={{ fontSize: 10, color: 'var(--text-muted)', marginRight: 4 }}
          >
            {draft.trim() ? 'MATCHING' : 'EXISTING'}
          </span>
          {availableSuggestions.map((s) => (
            <button
              key={s}
              type="button"
              className="chip"
              style={{
                cursor: 'pointer',
                fontSize: 11,
                background: 'var(--surface-2)',
                border: '1px dashed var(--border-subtle, rgba(255,255,255,0.2))',
              }}
              onClick={() => commit(s)}
              title="Click to add this existing tag"
              disabled={disabled}
            >
              + {s}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ─── Entries view (list + grid, with filters, sort and inline edit) ─────────

export type GtSortKey = 'name' | 'scope' | 'updated';
type GtSortDir = 'asc' | 'desc';

/** Scope sort key: workflow-scoped entries sort as '' (grouped first asc). */
const scopeTextOf = (e: GroundTruth) => e.nodeId ?? '';
const updatedMsOf = (e: GroundTruth) => new Date(e.updatedAt).getTime();

/** Ascending comparator per key; direction is applied by the caller. */
export function compareGt(a: GroundTruth, b: GroundTruth, key: GtSortKey): number {
  switch (key) {
    case 'name':
      return (a.name ?? '').localeCompare(b.name ?? '');
    case 'scope':
      return scopeTextOf(a).localeCompare(scopeTextOf(b));
    case 'updated':
      return updatedMsOf(a) - updatedMsOf(b);
  }
}

/**
 * Clickable, sortable header cell — same caret + `aria-sort` pattern as the
 * Workflows table's `SortableTh`, wired to the ground-truth sort keys. An
 * optional `filter` node renders after the label so a column can host both a
 * sort control and its filter popover.
 */
function SortableGtTh({
  label,
  sortKey,
  active,
  dir,
  onSort,
  width,
  align = 'left',
}: {
  label: string;
  sortKey: GtSortKey;
  active: boolean;
  dir: GtSortDir;
  onSort: (k: GtSortKey) => void;
  width?: number;
  align?: 'left' | 'right';
}) {
  return (
    <th
      style={{ ...thStyle, textAlign: align, width }}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        title={`Sort by ${label}`}
        style={{
          ...sortButtonStyle,
          flexDirection: align === 'right' ? 'row-reverse' : 'row',
          marginLeft: align === 'right' ? 'auto' : undefined,
        }}
      >
        {label}
        <span style={{ opacity: active ? 1 : 0.25, fontSize: 9 }}>
          {active ? (dir === 'asc' ? '▲' : '▼') : '▼'}
        </span>
      </button>
    </th>
  );
}

/** Compact "label + funnel" grouping used in the controls row. */
function FilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-1)' }}>
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 9,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: 'var(--text-secondary)',
        }}
      >
        {label}
      </span>
      {children}
    </span>
  );
}

/** Status pill shared by the list row and the grid card. */
function StatusBadge({ hasExpected, mismatch }: { hasExpected: boolean; mismatch: boolean }) {
  if (mismatch) return <span className="fk-badge--failed">schema mismatch</span>;
  if (hasExpected) return <span className="fk-badge--completed">Labeled</span>;
  return <span className="fk-badge--pending">Needs Labels</span>;
}

function GtTable({
  entries,
  nodes,
  workflowId,
  schemas,
  distinctTags,
  onChanged,
  footer,
}: {
  entries: GroundTruth[];
  nodes: { nodeKey: string; nodeType: string }[];
  workflowId: string;
  schemas: IWorkflowSchemas;
  distinctTags: string[];
  onChanged: () => void;
  footer?: React.ReactNode;
}) {
  // ── Filter (scope, multi-select) + sort state. Both client-side; the
  //   dataset is already in memory and tops out at the API's 200/page.
  //   Selected set values: 'workflow' OR a nodeKey. Empty set means "show all".
  const [scopeFilter, setScopeFilter] = useState<Set<string>>(new Set());
  const [labelFilter, setLabelFilter] = useState<Set<string>>(new Set());
  const [tagFilter, setTagFilter] = useState<Set<string>>(new Set());
  // Default: most-recently-updated first — preserved from the prior UI.
  const [sortKey, setSortKey] = useState<GtSortKey>('updated');
  const [sortDir, setSortDir] = useState<GtSortDir>('desc');
  // List is the default layout — cohesive with /workflows and /evals — with
  // the card grid behind the toggle. Persists to localStorage.
  const [viewMode, setViewMode] = useViewMode('groundTruth.viewMode', 'list');
  // When a grid card's Edit is clicked we flip to list view and open that
  // entry's inline editor; `openId` hands the target id to the matching row.
  const [openId, setOpenId] = useState<string | null>(null);

  const onSort = (k: GtSortKey) => {
    if (k === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(k);
      // Text columns read best A→Z; the date column high→low first.
      setSortDir(k === 'updated' ? 'desc' : 'asc');
    }
  };

  // Build the set of scope options from the actual entries + the workflow's
  // graph. Showing graph nodes that have zero entries lets the filter act as
  // a "show me what's missing" affordance.
  const scopeOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const e of entries) if (e.nodeId) seen.add(e.nodeId);
    for (const n of nodes) seen.add(n.nodeKey);
    return Array.from(seen).sort();
  }, [entries, nodes]);

  // ── Schema-based mismatch map. The single source of truth is the latest
  //   workflow version's published JSON Schemas (input + per-scope output).
  //   For each entry we precompute warnings against those schemas so the row
  //   renderer, the grid card and the Label Status filter share one result.
  // Per-entry mismatch flag. Only counts entries that are *actually*
  // mismatched against the schema — unlabeled entries (no expectedOutput yet)
  // are ignored on the expected side so the "schema mismatch" pill doesn't
  // double up with "Needs labels".
  const schemaMismatch = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const e of entries) {
      const input = compareToSchema(schemas.input, e.input as Record<string, unknown> | null);
      const target = e.nodeId ? (schemas.byNode.get(e.nodeId) ?? null) : schemas.output;
      const expected = e.expectedOutput
        ? compareToSchema(target, e.expectedOutput as Record<string, unknown>)
        : null;
      map.set(e.id, !!(input || expected));
    }
    return map;
  }, [entries, schemas]);

  const filtered = useMemo(() => {
    let out = entries;
    if (scopeFilter.size > 0) {
      out = out.filter((e) =>
        e.nodeId === null ? scopeFilter.has('workflow') : scopeFilter.has(e.nodeId),
      );
    }
    if (labelFilter.size > 0) {
      out = out.filter((e) => {
        const has = !!e.expectedOutput;
        const mismatch = schemaMismatch.get(e.id) === true;
        if (labelFilter.has('schema-mismatch') && mismatch) return true;
        if (labelFilter.has('has') && has && !mismatch) return true;
        if (labelFilter.has('needs') && !has && !mismatch) return true;
        return false;
      });
    }
    if (tagFilter.size > 0) {
      // hasSome semantics — entry passes if it has AT LEAST ONE selected tag.
      // Mirrors the server-side `tags: { hasSome }` filter.
      out = out.filter((e) => (e.tags ?? []).some((t) => tagFilter.has(t)));
    }
    return out;
  }, [entries, scopeFilter, labelFilter, tagFilter, schemaMismatch]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const cmp = compareGt(a, b, sortKey);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  // Open a specific entry in the list editor (from a grid card).
  const editEntry = (id: string) => {
    setOpenId(id);
    setViewMode('list');
  };

  // Empty of entries entirely — no filters/toggle to show.
  if (entries.length === 0) {
    return (
      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: 'var(--sp-4)', textAlign: 'center' }} className="t-muted">
          No ground truth entries for this workflow yet. Use the quick-add row below to create one.
        </div>
        {footer}
      </div>
    );
  }

  const thProps = (key: GtSortKey) => ({
    sortKey: key,
    active: sortKey === key,
    dir: sortDir,
    onSort,
  });

  const scopeFilterControl = (
    <IconFilter
      title="Filter scope"
      selected={scopeFilter}
      onChange={setScopeFilter}
      options={[
        { value: 'workflow', label: 'Workflow', hint: 'Entries scored end-to-end' },
        ...scopeOptions.map((k) => ({ value: k, label: k, mono: true, group: 'NODES' })),
      ]}
    />
  );
  const labelFilterControl = (
    <IconFilter
      title="Filter label status"
      selected={labelFilter}
      onChange={setLabelFilter}
      options={[
        { value: 'has', label: 'Has labels', hint: 'expectedOutput is set' },
        { value: 'needs', label: 'Needs labels', hint: 'awaiting expected output' },
        {
          value: 'schema-mismatch',
          label: 'Schema mismatch',
          hint: "input/output keys don't match the workflow's published schemas",
        },
      ]}
    />
  );
  const tagFilterControl = (
    <IconFilter
      title="Filter tags"
      selected={tagFilter}
      onChange={setTagFilter}
      options={
        distinctTags.length === 0
          ? []
          : distinctTags.map((t) => ({ value: t, label: t, mono: true }))
      }
    />
  );

  return (
    <>
      {/* Filters (left) + list/grid toggle (right) — same controls-row
          placement the Workflows and Evals lists use. */}
      <div style={{ ...controlsRow, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', flexWrap: 'wrap' }}
        >
          <FilterGroup label="Scope">{scopeFilterControl}</FilterGroup>
          <FilterGroup label="Label">{labelFilterControl}</FilterGroup>
          <FilterGroup label="Tags">{tagFilterControl}</FilterGroup>
        </div>
        <ViewToggle value={viewMode} onChange={setViewMode} ariaLabel="Ground truth layout" />
      </div>

      {viewMode === 'grid' ? (
        <>
          {sorted.length === 0 ? (
            <div className="card" style={{ padding: 0 }}>
              <div style={{ padding: 'var(--sp-4)', textAlign: 'center' }} className="t-muted">
                No entries match the current filters.
              </div>
              {footer}
            </div>
          ) : (
            <>
              <div style={cardGrid}>
                {sorted.map((g) => (
                  <GtCard
                    key={g.id}
                    entry={g}
                    nodes={nodes}
                    mismatch={schemaMismatch.get(g.id) === true}
                    onEdit={() => editEntry(g.id)}
                  />
                ))}
              </div>
              {footer ? (
                <div className="card" style={{ padding: 0, marginTop: 'var(--sp-3)' }}>
                  {footer}
                </div>
              ) : null}
            </>
          )}
        </>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 880 }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: 36 }} aria-hidden={true} />
                  <SortableGtTh label="Name" {...thProps('name')} />
                  <SortableGtTh label="Scope" width={200} {...thProps('scope')} />
                  <th style={{ ...thStyle, width: 160 }}>Label Status</th>
                  <th style={{ ...thStyle, width: 160 }}>Tags</th>
                  <SortableGtTh label="Last Updated" width={180} {...thProps('updated')} />
                </tr>
              </thead>
              <tbody>
                {sorted.map((g) => (
                  <GtRow
                    key={g.id}
                    entry={g}
                    nodes={nodes}
                    workflowId={workflowId}
                    schemas={schemas}
                    distinctTags={distinctTags}
                    onChanged={onChanged}
                    forceOpenId={openId}
                  />
                ))}
                {sorted.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ ...tdStyle, textAlign: 'center' }} className="t-muted">
                      No entries match the current filters.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
          {footer}
        </div>
      )}
    </>
  );
}

// Row cells keep the shared 14/16 padding + 13px text but stay top-aligned so
// the multi-line Name cell (name + notes + schema warnings) reads cleanly.
const gtTdStyle: CSSProperties = {
  ...tdStyle,
  verticalAlign: 'top',
};

// Roomier style for the expanded inline-edit panel row.
const cellStyle: CSSProperties = {
  padding: 'var(--sp-2) var(--sp-3)',
  borderTop: '1px solid var(--border-subtle, rgba(255,255,255,0.08))',
  fontSize: 13,
  verticalAlign: 'top',
};

/**
 * Read-only grid card for a single ground-truth entry — cohesive with the
 * Workflows card grid. Shows name, scope, status, tags, and a compact input
 * preview. Editing is deferred to the list view: clicking the card (or its
 * Edit affordance) flips to the list and opens this entry's inline editor.
 */
function GtCard({
  entry,
  nodes,
  mismatch,
  onEdit,
}: {
  entry: GroundTruth;
  nodes: { nodeKey: string; nodeType: string }[];
  mismatch: boolean;
  onEdit: () => void;
}) {
  const nodeType = entry.nodeId
    ? nodes.find((n) => n.nodeKey === entry.nodeId)?.nodeType
    : undefined;
  const inputKeys = Object.keys(
    (entry.input && typeof entry.input === 'object' ? entry.input : {}) as Record<string, unknown>,
  );
  const inputPreview =
    inputKeys.length > 0 ? inputKeys.join(', ') : JSON.stringify(entry.input ?? {});

  return (
    <button
      type="button"
      style={cardChrome}
      onClick={onEdit}
      onMouseEnter={cardHoverIn}
      onMouseLeave={cardHoverOut}
      title="Edit this entry"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 'var(--sp-2)',
        }}
      >
        <span
          style={{
            fontSize: 'var(--fs-sm)',
            fontWeight: 'var(--fw-med)',
            color: 'var(--text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
          title={entry.name ?? undefined}
        >
          {entry.name ? entry.name : <span className="t-muted">&lt;unnamed&gt;</span>}
        </span>
        <span style={{ flexShrink: 0 }}>
          <StatusBadge hasExpected={!!entry.expectedOutput} mismatch={mismatch} />
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-1)', flexWrap: 'wrap' }}>
        {entry.nodeId ? (
          <>
            <span className="badge badge--neutral" style={{ fontFamily: 'var(--font-mono)' }}>
              {entry.nodeId}
            </span>
            {nodeType ? <span className="t-xs t-muted">{nodeType}</span> : null}
          </>
        ) : (
          <span className="badge badge--neutral">workflow</span>
        )}
      </div>

      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }} title="Input keys">
        <span className="t-mono-label" style={{ fontSize: 10, marginRight: 6 }}>
          INPUT
        </span>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'inline-block',
            maxWidth: '100%',
            verticalAlign: 'bottom',
          }}
        >
          {inputPreview}
        </span>
      </div>

      {entry.tags && entry.tags.length > 0 ? (
        <TagEditor
          tags={entry.tags}
          readOnly={true}
          ariaLabel={`Tags for ${entry.name ?? 'entry'}`}
        />
      ) : null}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 'var(--fs-xs)',
          color: 'var(--text-secondary)',
          gap: 'var(--sp-2)',
        }}
      >
        <span>Updated {formatDateTime(entry.updatedAt)}</span>
        <span style={{ color: 'var(--text-primary)' }}>Edit</span>
      </div>
    </button>
  );
}

function GtRow({
  entry,
  nodes,
  workflowId,
  schemas,
  distinctTags,
  onChanged,
  forceOpenId,
}: {
  entry: GroundTruth;
  nodes: { nodeKey: string; nodeType: string }[];
  workflowId: string;
  schemas: IWorkflowSchemas;
  distinctTags: string[];
  onChanged: () => void;
  /** When this matches the row's id (e.g. the user clicked Edit on a grid
   *  card), expand this row's inline editor. */
  forceOpenId?: string | null;
}) {
  // ── Warnings derived from the workflow author's published JSON Schemas
  //   (latest version). The schemas are the single source of truth for
  //   "what this entry should look like" — siblings are no longer consulted.
  //   We compare top-level keys only; deep validation is the runner's job.
  const inputSchemaWarning = useMemo(
    () => compareToSchema(schemas.input, entry.input as Record<string, unknown> | null),
    [schemas.input, entry.input],
  );
  const expectedSchemaTarget = entry.nodeId
    ? (schemas.byNode.get(entry.nodeId) ?? null)
    : schemas.output;
  // Note: we deliberately compare even when expectedOutput is null — an
  // unlabeled entry should still surface "missing X" so the user knows
  // which keys the workflow contract expects them to populate.
  const expectedSchemaWarning = useMemo(
    () =>
      compareToSchema(expectedSchemaTarget, entry.expectedOutput as Record<string, unknown> | null),
    [expectedSchemaTarget, entry.expectedOutput],
  );
  /** Top-level keys the workflow contract declares for this entry's expected output. */
  const expectedSchemaKeys = useMemo(() => {
    const props = (expectedSchemaTarget?.properties ?? null) as Record<string, unknown> | null;
    return props ? new Set(Object.keys(props)) : new Set<string>();
  }, [expectedSchemaTarget]);
  /** Skeleton seed for the "Start labeling" button — declared keys with
   *  type-appropriate blanks pulled from the workflow's JSON Schema. */
  const expectedSchemaSeed = useMemo<Record<string, unknown> | undefined>(() => {
    const props = (expectedSchemaTarget?.properties ?? null) as Record<string, unknown> | null;
    if (!props) return undefined;
    const seed: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(props)) seed[k] = blankForJsonSchemaType(v);
    return Object.keys(seed).length > 0 ? seed : undefined;
  }, [expectedSchemaTarget]);
  const client = useClient();
  const [open, setOpen] = useState(false);
  const [expected, setExpected] = useState<Record<string, unknown> | null>(
    (entry.expectedOutput as Record<string, unknown> | null) ?? null,
  );
  // Local staging for tags so edits are buffered until the user hits Save —
  // same UX contract as `expected` above. Without this we'd PATCH on every
  // chip add/remove, which both surprises the user and burns API calls.
  const [tagsLocal, setTagsLocal] = useState<string[]>(entry.tags ?? []);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Re-sync when the upstream entry changes (e.g. after refetch).
  useEffect(() => {
    setExpected((entry.expectedOutput as Record<string, unknown> | null) ?? null);
    setTagsLocal(entry.tags ?? []);
  }, [entry.id, entry.name, entry.expectedOutput, entry.tags]);

  // Open when a grid card handed this entry's id over for editing.
  useEffect(() => {
    if (forceOpenId && forceOpenId === entry.id) setOpen(true);
  }, [forceOpenId, entry.id]);

  const save = useMutation({
    mutationFn: () => {
      // Name and notes are owned by the inline pencil edits on the row, not this form.
      return client.http
        .patch(`v1/ground-truth/${entry.id}`, {
          json: {
            expectedOutput: expected,
            tags: tagsLocal,
          },
        })
        .json<GroundTruth>();
    },
    onSuccess: () => {
      setSavedAt(Date.now());
      onChanged();
    },
  });

  // Name- and notes-only patches — used by the inline pencil edits on the
  // row. Avoids having to expand the row to rename or add a note.
  const saveName = useMutation({
    mutationFn: (next: string) =>
      client.http
        .patch(`v1/ground-truth/${entry.id}`, { json: { name: next || undefined } })
        .json<GroundTruth>(),
    onSuccess: () => onChanged(),
  });
  const saveNotes = useMutation({
    mutationFn: (next: string) =>
      client.http
        .patch(`v1/ground-truth/${entry.id}`, { json: { notes: next } })
        .json<GroundTruth>(),
    onSuccess: () => onChanged(),
  });
  const remove = useMutation({
    mutationFn: () => client.http.delete(`v1/ground-truth/${entry.id}`).text(),
    onSuccess: () => onChanged(),
  });

  const nodeType = entry.nodeId
    ? nodes.find((n) => n.nodeKey === entry.nodeId)?.nodeType
    : undefined;
  const typeTagStyle: CSSProperties = {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: 999,
    border: '1px solid var(--border-subtle, rgba(255,255,255,0.18))',
    background: 'var(--surface-2, rgba(255,255,255,0.04))',
    color: 'var(--text-muted)',
    fontSize: 10,
    letterSpacing: 0.3,
    lineHeight: 1.4,
    marginTop: 2,
  };
  const scopeLabel = entry.nodeId ? (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <code style={{ fontSize: 12, color: 'var(--text-primary)' }}>{entry.nodeId}</code>
      {nodeType ? <span style={typeTagStyle}>{nodeType}</span> : null}
    </div>
  ) : (
    <span className="t-xs t-muted">workflow</span>
  );

  return (
    <>
      <tr style={{ cursor: 'pointer' }} onClick={() => setOpen((v) => !v)}>
        <td style={gtTdStyle} aria-hidden={true}>
          <span style={{ display: 'inline-block', width: 12 }}>{open ? '▾' : '▸'}</span>
        </td>
        <td style={gtTdStyle}>
          {/* Each inline-editable wrapper stops click propagation so the
              row's expand toggle only fires when the user clicks the
              cell's whitespace (not when they click to start editing
              the name or the notes). */}
          <span onClick={(e: MouseEvent) => e.stopPropagation()}>
            <InlineEditableText
              value={entry.name ?? ''}
              onSubmit={(next) => saveName.mutate(next)}
              isPending={saveName.isPending}
              ariaLabel="Entry name"
              placeholder="Name this entry…"
              emptyDisplay="<unnamed>"
              textStyle={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 13 }}
              emptyTextStyle={{
                fontStyle: 'italic',
                color: 'var(--text-muted)',
                fontSize: 13,
                fontWeight: 500,
              }}
              validate={(next) => (next.length > 200 ? 'Max 200 characters' : null)}
            />
          </span>
          <div
            style={{ marginTop: 2, maxWidth: 360 }}
            onClick={(e: MouseEvent) => e.stopPropagation()}
          >
            <InlineEditableText
              value={entry.notes ?? ''}
              onSubmit={(next) => saveNotes.mutate(next)}
              isPending={saveNotes.isPending}
              ariaLabel="Entry notes"
              placeholder="Add a note…"
              emptyDisplay="<no notes>"
              textStyle={{
                fontSize: 12,
                color: 'var(--text-muted)',
              }}
              emptyTextStyle={{
                fontSize: 12,
                fontStyle: 'italic',
                color: 'var(--text-subtle, rgba(255,255,255,0.35))',
              }}
            />
          </div>
          {(inputSchemaWarning || expectedSchemaWarning) && (
            <div style={{ marginTop: 'var(--sp-2)', fontSize: 11, maxWidth: 360 }}>
              {inputSchemaWarning && (
                <div
                  style={{ color: 'var(--status-warning, #fbbf24)' }}
                  title="Compared against the workflow's published input schema"
                >
                  <strong>Input:</strong>{' '}
                  {inputSchemaWarning.missing.length > 0 && (
                    <span>
                      missing {JSON.stringify(inputSchemaWarning.missing)}
                      {inputSchemaWarning.missingRequired.length > 0 ? ' (required)' : ''}{' '}
                    </span>
                  )}
                  {inputSchemaWarning.extra.length > 0 && (
                    <span>extra {JSON.stringify(inputSchemaWarning.extra)}</span>
                  )}
                </div>
              )}
              {expectedSchemaWarning && (
                <div
                  style={{ color: 'var(--status-warning, #fbbf24)' }}
                  title={
                    entry.nodeId
                      ? "Compared against this node's outputSchema in the workflow graph"
                      : "Compared against the workflow's published outputSchema"
                  }
                >
                  <strong>Expected:</strong>{' '}
                  {expectedSchemaWarning.missing.length > 0 && (
                    <span>
                      missing {JSON.stringify(expectedSchemaWarning.missing)}
                      {expectedSchemaWarning.missingRequired.length > 0 ? ' (required)' : ''}{' '}
                    </span>
                  )}
                  {expectedSchemaWarning.extra.length > 0 && (
                    <span>extra {JSON.stringify(expectedSchemaWarning.extra)}</span>
                  )}
                </div>
              )}
            </div>
          )}
        </td>
        <td style={gtTdStyle}>{scopeLabel}</td>
        <td style={gtTdStyle}>
          {/* Only flag as a "mismatch" when there's an actual labeled value
              diverging from the schema (or input doesn't match). An unlabeled
              entry still shows the missing-keys suggestion in the notes area
              but keeps the friendlier "awaiting label" badge. */}
          {inputSchemaWarning || (entry.expectedOutput && expectedSchemaWarning) ? (
            <span className="fk-badge--failed">schema mismatch</span>
          ) : entry.expectedOutput ? (
            <span className="fk-badge--completed">Labeled</span>
          ) : (
            <span className="fk-badge--pending">Needs Labels</span>
          )}
        </td>
        <td style={gtTdStyle}>
          {entry.tags && entry.tags.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {entry.tags.map((t) => (
                <span key={t} className="chip">
                  {t}
                </span>
              ))}
            </div>
          ) : (
            <span className="t-xs t-muted">—</span>
          )}
        </td>
        <td
          style={{ ...gtTdStyle, color: 'var(--text-secondary)' }}
          className="t-mono t-xs"
          title={`Created ${formatDateTime(entry.createdAt)}`}
        >
          {formatDateTime(entry.updatedAt)}
        </td>
      </tr>
      {open ? (
        <tr>
          <td colSpan={6} style={{ ...cellStyle, background: 'var(--surface-2)' }}>
            <div
              onClick={(e: MouseEvent) => e.stopPropagation()}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 'var(--sp-3)',
              }}
            >
              <div>
                <span className="field-label">
                  Input (read-only)
                  <InfoHint
                    text="Input is immutable — changing it would change inputHash and invalidate past evaluation results."
                    side="right"
                  />
                </span>
                <div style={{ margin: 'var(--sp-1) 0', maxHeight: 280, overflow: 'auto' }}>
                  <JsonViewer value={entry.input} />
                </div>
                <div className="field" style={{ marginTop: 'var(--sp-2)' }}>
                  <span className="field-label">
                    Tags
                    <InfoHint
                      text="Free-form tags for grouping (e.g. regression, edge-case). Normalized to lowercase. Filter the list by tag using the column header."
                      side="right"
                    />
                  </span>
                  <TagInput
                    value={tagsLocal}
                    onChange={setTagsLocal}
                    suggestions={distinctTags}
                    disabled={save.isPending}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
                <div className="field">
                  <span className="field-label">
                    Expected output
                    <InfoHint
                      text={
                        entry.nodeId
                          ? "Scored against this node's output during evaluation."
                          : "Scored against the workflow's final output during evaluation."
                      }
                      side="right"
                    />
                  </span>
                  <JsonObjectBuilder
                    value={expected}
                    onChange={setExpected}
                    knownTopLevelKeys={expectedSchemaKeys.size > 0 ? expectedSchemaKeys : undefined}
                    unknownKeyLabel="Not declared in the workflow's output schema for this scope:"
                    seedValue={expectedSchemaSeed}
                  />
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 'var(--sp-2)',
                    alignItems: 'center',
                    marginTop: 'var(--sp-2)',
                  }}
                >
                  <button
                    type="button"
                    className="btn btn--primary btn--sm"
                    onClick={() => save.mutate()}
                    disabled={save.isPending}
                  >
                    {save.isPending ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    className="btn btn--danger btn--sm"
                    onClick={() => {
                      if (window.confirm('Soft-delete this entry?')) remove.mutate();
                    }}
                    disabled={remove.isPending}
                  >
                    Delete
                  </button>
                  {save.error ? (
                    <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
                      {(save.error as Error).message}
                    </span>
                  ) : savedAt ? (
                    <span className="t-xs" style={{ color: 'var(--status-success)' }}>
                      Saved.
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </td>
        </tr>
      ) : null}
      {/* keep workflowId in scope for lint */}
      {workflowId === '__never__' ? <tr /> : null}
    </>
  );
}

// ─── Quick-add row ──────────────────────────────────────────────────────────

function QuickAddRow({
  workflowId,
  nodes,
  entries,
  schemas,
  distinctTags,
  onAdded,
  open,
  setOpen,
  containerRef,
}: {
  workflowId: string;
  nodes: { nodeKey: string; nodeType: string }[];
  entries: GroundTruth[];
  schemas: IWorkflowSchemas;
  distinctTags: string[];
  onAdded: () => void;
  open: boolean;
  setOpen: (v: boolean) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
}) {
  const client = useClient();
  const [name, setName] = useState('');
  const [scope, setScope] = useState<'workflow' | 'node'>('workflow');
  const [nodeKey, setNodeKey] = useState('');
  const [input, setInput] = useState<Record<string, unknown>>({});
  const [expected, setExpected] = useState<Record<string, unknown> | null>(null);
  const [notes, setNotes] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  // Track whether the user has hand-edited input/expected since the last
  // scope change. We only auto-prefill from the schema when both are still
  // at their pristine defaults — never overwrite real work.
  const [inputTouched, setInputTouched] = useState(false);
  // Reserved for parity with inputTouched if we re-introduce auto-prefill.
  const [, setExpectedTouched] = useState(false);
  // Expected output is *opt-in* — kept hidden until the user explicitly
  // adds it (button click below). Avoids cluttering the form when the user
  // just wants to drop an unlabeled input in.
  const [showExpected, setShowExpected] = useState(false);

  const nameRef = useRef<HTMLInputElement | null>(null);
  const prevOpenRef = useRef(open);

  // On open: focus the name input AND, if the user hasn't already chosen a
  // scope this session, default it to the most-recently-updated entry's
  // scope. The "this session" guard means hitting Cancel + Add Entry again
  // doesn't keep clobbering the user's choice.
  useEffect(() => {
    if (!open || prevOpenRef.current) {
      prevOpenRef.current = open;
      return;
    }
    prevOpenRef.current = open;
    setTimeout(() => nameRef.current?.focus(), 30);
    if (scope === 'workflow' && nodeKey === '' && entries.length > 0) {
      const recent = [...entries].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      )[0];
      if (recent?.nodeId) {
        setScope('node');
        setNodeKey(recent.nodeId);
      }
    }
    // Intentional: only run on open transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Schema template, derived entirely from the latest workflow version's
  //   published JSON Schemas. `inputProps` is the workflow trigger input;
  //   `expectedProps` is workflow-output for workflow scope, the selected
  //   node's outputSchema for node scope. Either may be null when the author
  //   didn't publish that layer.
  const schema = useMemo(() => {
    const inputSchema = schemas.input;
    const expectedTarget =
      scope === 'node' ? (schemas.byNode.get(nodeKey) ?? null) : schemas.output;
    const inputProps = (inputSchema?.properties ?? null) as Record<string, unknown> | null;
    const expectedProps = (expectedTarget?.properties ?? null) as Record<string, unknown> | null;
    if (!inputProps && !expectedProps) return null;
    return {
      inputKeys: inputProps ? new Set(Object.keys(inputProps)) : new Set<string>(),
      expectedKeys: expectedProps ? new Set(Object.keys(expectedProps)) : new Set<string>(),
      inputProps,
      expectedProps,
      /** Stable identity for the prefill effect's dep array. */
      key: `${scope}:${nodeKey}`,
    };
  }, [schemas, scope, nodeKey]);

  /** Build a fresh input skeleton from the current scope's input schema. */
  const buildInputSeed = (): Record<string, unknown> => {
    const seed: Record<string, unknown> = {};
    if (schema?.inputProps) {
      for (const [k, v] of Object.entries(schema.inputProps)) {
        seed[k] = blankForJsonSchemaType(v);
      }
    }
    return seed;
  };

  // ── Auto-prefill the *input* field on scope change. Expected output is
  //   opt-in (see `showExpected`) — never auto-prefilled.
  useEffect(() => {
    if (!schema) return;
    if (!inputTouched && Object.keys(input).length === 0) {
      const seed = buildInputSeed();
      if (Object.keys(seed).length > 0) setInput(seed);
    }
    // Re-run when the target schema changes (scope/node switch). Touched
    // flags are reset elsewhere when the user explicitly switches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema?.key]);

  /**
   * Reveal the expected-output section. Seeds the JSON builder with the
   * declared schema keys (typed blanks) so the user only has to fill values.
   */
  const addExpectedOutput = () => {
    setShowExpected(true);
    if (schema?.expectedProps && expected === null) {
      const prefilled: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(schema.expectedProps)) {
        prefilled[k] = blankForJsonSchemaType(v);
      }
      if (Object.keys(prefilled).length > 0) setExpected(prefilled);
      else setExpected({});
    } else if (expected === null) {
      setExpected({});
    }
  };

  const inputEmpty = Object.keys(input).length === 0;

  // ── Live schema warnings against the workflow author's published contract.
  //   Input is the workflow trigger input (always workflow-level). Expected
  //   output is workflow-level for workflow scope, node-level for node scope.
  const inputSchemaWarning = useMemo(
    () => compareToSchema(schemas.input, input),
    [schemas.input, input],
  );
  const expectedSchemaTarget =
    scope === 'node' ? (schemas.byNode.get(nodeKey) ?? null) : schemas.output;
  const expectedSchemaWarning = useMemo(
    () => compareToSchema(expectedSchemaTarget, expected),
    [expectedSchemaTarget, expected],
  );

  const create = useMutation({
    mutationFn: () => {
      if (inputEmpty) {
        return Promise.reject(new Error('Input must have at least one key'));
      }
      return client.http
        .post('v1/ground-truth', {
          json: {
            workflowId,
            nodeId: scope === 'node' && nodeKey ? nodeKey : undefined,
            input,
            expectedOutput: expected,
            name: name.trim() || undefined,
            notes: notes.trim() || undefined,
            tags: tags.length > 0 ? tags : undefined,
          },
        })
        .json<GroundTruth>();
    },
    onSuccess: () => {
      // Reset for the next entry — keep scope/node choice for repetitive
      // labeling. Re-seed the input from the current scope's schema so the
      // user lands on the same starting shape they had on first open.
      setName('');
      setInput(buildInputSeed());
      setExpected(null);
      setNotes('');
      setTags([]);
      setInputTouched(false);
      setExpectedTouched(false);
      setShowExpected(false);
      onAdded();
    },
  });

  if (!open) {
    return (
      <div
        ref={containerRef}
        style={{
          padding: 'var(--sp-2) var(--sp-3)',
          borderTop: '1px solid var(--border-subtle, rgba(255,255,255,0.12))',
          background: 'var(--surface-2)',
        }}
      >
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setOpen(true)}>
          + Add ground truth
        </button>
      </div>
    );
  }

  const canSubmit =
    !inputEmpty && !create.isPending && (scope === 'workflow' || nodeKey.length > 0);

  return (
    <div
      ref={containerRef}
      style={{
        padding: 'var(--sp-3)',
        borderTop: '1px solid var(--border-subtle, rgba(255,255,255,0.12))',
        background: 'var(--surface-2)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-2)',
      }}
    >
      <div
        style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}
      >
        <label className="field" style={{ minWidth: 200, flex: 1 }}>
          <span className="field-label">Name</span>
          <input
            ref={nameRef}
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="entry-1"
          />
        </label>
        <label className="field" style={{ minWidth: 160 }}>
          <span className="field-label">Scope</span>
          <select
            className="select-native"
            value={scope}
            onChange={(e) => setScope(e.target.value as 'workflow' | 'node')}
          >
            <option value="workflow">Workflow</option>
            <option value="node">Specific node</option>
          </select>
        </label>
        {scope === 'node' ? (
          <label className="field" style={{ minWidth: 200 }}>
            <span className="field-label">Node</span>
            <select
              className="select-native"
              value={nodeKey}
              onChange={(e) => setNodeKey(e.target.value)}
            >
              <option value="">
                {nodes.length === 0 ? '— No nodes in graph —' : '— Pick a node —'}
              </option>
              {nodes.map((n) => (
                <option key={n.nodeKey} value={n.nodeKey}>
                  {n.nodeKey} ({n.nodeType})
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)' }}>
        <div className="field">
          <span className="field-label">Input</span>
          <JsonObjectBuilder
            value={input}
            onChange={(v) => {
              setInput(v ?? {});
              setInputTouched(true);
            }}
            allowNull={false}
            knownTopLevelKeys={schema?.inputKeys}
            unknownKeyLabel="Not declared in the workflow's input schema:"
          />
          {inputEmpty ? (
            <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
              Input must have at least one key
            </span>
          ) : null}
          {inputSchemaWarning && (
            <div
              className="t-xs"
              style={{ color: 'var(--status-warning, #fbbf24)', marginTop: 4 }}
              title="Compared against the workflow's published input schema"
            >
              <strong>vs workflow input schema:</strong>{' '}
              {inputSchemaWarning.missing.length > 0 && (
                <span>
                  missing {JSON.stringify(inputSchemaWarning.missing)}
                  {inputSchemaWarning.missingRequired.length > 0 ? ' (required)' : ''}{' '}
                </span>
              )}
              {inputSchemaWarning.extra.length > 0 && (
                <span>extra {JSON.stringify(inputSchemaWarning.extra)}</span>
              )}
            </div>
          )}
        </div>
        <div className="field">
          {showExpected ? (
            <>
              <span className="field-label">Expected output (optional)</span>
              <JsonObjectBuilder
                value={expected}
                onChange={(v) => {
                  setExpected(v);
                  setExpectedTouched(true);
                }}
                knownTopLevelKeys={schema?.expectedKeys}
                unknownKeyLabel="Not declared in the workflow's output schema for this scope:"
              />
              {expectedSchemaWarning && (
                <div
                  className="t-xs"
                  style={{ color: 'var(--status-warning, #fbbf24)', marginTop: 4 }}
                  title={
                    scope === 'node'
                      ? "Compared against the selected node's outputSchema in the workflow graph"
                      : "Compared against the workflow's published outputSchema"
                  }
                >
                  <strong>vs {scope === 'node' ? 'node' : 'workflow'} output schema:</strong>{' '}
                  {expectedSchemaWarning.missing.length > 0 && (
                    <span>
                      missing {JSON.stringify(expectedSchemaWarning.missing)}
                      {expectedSchemaWarning.missingRequired.length > 0 ? ' (required)' : ''}{' '}
                    </span>
                  )}
                  {expectedSchemaWarning.extra.length > 0 && (
                    <span>extra {JSON.stringify(expectedSchemaWarning.extra)}</span>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <span className="field-label">Expected output</span>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={addExpectedOutput}
                style={{ alignSelf: 'flex-start' }}
              >
                + Add expected output
              </button>
              <span className="t-xs" style={{ color: 'var(--text-muted)', marginTop: 4 }}>
                Leave unlabeled to score this entry later, or click to seed it with the
                workflow&apos;s output-schema keys.
              </span>
            </>
          )}
        </div>
      </div>
      <label className="field">
        <span className="field-label">Notes (optional)</span>
        <textarea
          className="textarea"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Why is this the right answer? Edge case it covers? Source ticket?"
        />
      </label>
      <div className="field">
        <span className="field-label">Tags (optional)</span>
        <TagInput value={tags} onChange={setTags} suggestions={distinctTags} />
      </div>
      <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          onClick={() => create.mutate()}
          disabled={!canSubmit}
        >
          {create.isPending ? 'Adding…' : 'Add entry'}
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setOpen(false)}>
          Cancel
        </button>
        {create.error ? (
          <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
            {(create.error as Error).message}
          </span>
        ) : null}
      </div>
    </div>
  );
}
